import React, { useState, useEffect } from "react";
import { TbArrowLeft, TbArrowUp, TbArrowDown, TbX, TbPlus } from "react-icons/tb";
import { useAssessmentTemplate, useReviewLibrary } from "../../hooks/useAssessmentTemplates";
import { Button, Alert, Field, Modal, TopbarSlot } from "../../components";
import { fmtDate } from "../../lib/dates";
import { CATEGORIES } from "./LibraryItemDrawer";
import "./Reviews.css";

const ITEMS_PER_CATEGORY = 9;

// The template builder: categories left, the nine slots centre, readiness right.
//
// Reordering is buttons, not drag. A nine-item list that a consultant touches a few
// times a year does not justify a drag implementation, and buttons work from the
// keyboard and on a touch screen without any extra work.
//
// The draft is saved explicitly rather than on every change. A category is briefly
// invalid while it is being filled — that is normal and is exactly what the publish rule
// is there to catch, so there is no reason to persist each intermediate state.
const TemplateBuilder = ({ id, onBack }) => {
  const { template, versions, loading, error, busy, save, publish } = useAssessmentTemplate(id);
  const { items: library, reload: reloadLibrary } = useReviewLibrary();

  const [draft, setDraft] = useState(null);
  const [activeName, setActiveName] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [adding, setAdding] = useState(false);
  const [notice, setNotice] = useState("");
  const [failure, setFailure] = useState("");
  const [newCategory, setNewCategory] = useState(false);

  useEffect(() => {
    if (!template) return;
    const categories = (template.content?.categories || []).map((c) => ({
      name: c.name,
      active: c.active !== false,
      itemIds: c.itemIds || [],
    }));
    setDraft({ categories });
    setActiveName((prev) => prev ?? categories[0]?.name ?? null);
    setDirty(false);
  }, [template]);

  if (loading || !draft) return <div className="v2-page"><p className="v2-hint">Loading…</p></div>;
  if (!template) {
    return (
      <div className="v2-page">
        <Alert>{error || "That template could not be loaded."}</Alert>
        <Button onClick={onBack}><TbArrowLeft size={15} /> Templates</Button>
      </div>
    );
  }

  const current = draft.categories.find((c) => c.name === activeName) || draft.categories[0];
  const byId = new Map(library.map((i) => [i.id, i]));

  const update = (fn) => { setDraft((d) => fn(d)); setDirty(true); setNotice(""); setFailure(""); };

  const patchCategory = (name, patch) =>
    update((d) => ({
      ...d,
      categories: d.categories.map((c) => (c.name === name ? { ...c, ...patch } : c)),
    }));

  const addCategory = (name) => {
    const clean = String(name || "").trim();
    if (!clean) return;
    if (draft.categories.some((c) => c.name.toLowerCase() === clean.toLowerCase())) {
      setFailure(`There is already a category called ${clean}.`);
      return;
    }
    update((d) => ({ ...d, categories: [...d.categories, { name: clean, active: true, itemIds: [] }] }));
    setActiveName(clean);
    setNewCategory(false);
  };

  const removeCategory = (name) => {
    update((d) => ({ ...d, categories: d.categories.filter((c) => c.name !== name) }));
    if (activeName === name) setActiveName(draft.categories.find((c) => c.name !== name)?.name ?? null);
  };

  const move = (index, delta) => {
    const next = [...current.itemIds];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    patchCategory(current.name, { itemIds: next });
  };

  const removeItem = (itemId) =>
    patchCategory(current.name, { itemIds: current.itemIds.filter((x) => x !== itemId) });

  const addItems = (ids) => {
    const merged = [...current.itemIds, ...ids.filter((x) => !current.itemIds.includes(x))];
    patchCategory(current.name, { itemIds: merged });
    setAdding(false);
  };

  const activeCategories = draft.categories.filter((c) => c.active);
  const full = activeCategories.filter((c) => c.itemIds.length === ITEMS_PER_CATEGORY).length;
  const readiness = activeCategories.length
    ? Math.round((full / activeCategories.length) * 100)
    : 0;

  const onSave = async () => {
    const ok = await save(draft);
    if (ok) { setDirty(false); setNotice("Draft saved."); setTimeout(() => setNotice(""), 2500); }
  };

  const onPublish = async () => {
    if (dirty) {
      const ok = await save(draft);
      if (!ok) return;
      setDirty(false);
    }
    const result = await publish();
    if (result.ok) {
      setNotice(`Published as version ${result.version}.`);
      setFailure("");
    } else {
      const short = (result.incomplete || []).map((c) => `${c.category} has ${c.count}`).join(", ");
      setFailure(short ? `${result.error}: ${short}.` : result.error);
    }
  };

  return (
    <div className="v2-page">
      <TopbarSlot>
        <Button onClick={onSave} disabled={busy || !dirty}>{busy ? "Saving…" : "Save draft"}</Button>
        <Button variant="primary" onClick={onPublish} disabled={busy}>Publish</Button>
      </TopbarSlot>

      <div className="v2-pagehead">
        <div>
          <div className="v2-eyebrow">
            Template · {template.current_version > 0 ? `version ${template.current_version}` : "never published"}
          </div>
          <h2>{template.name}</h2>
          <p>{template.description || "No description."}</p>
        </div>
        <div className="v2-pagehead-actions">
          {dirty && <span className="v2-rbadge v2-rbadge--accent">UNSAVED CHANGES</span>}
          {!dirty && template.has_unpublished_changes && (
            <span className="v2-rbadge v2-rbadge--accent">UNPUBLISHED CHANGES</span>
          )}
          <Button onClick={onBack}><TbArrowLeft size={15} /> Templates</Button>
        </div>
      </div>

      <Alert>{error || failure}</Alert>
      {notice && <Alert tone="success">{notice}</Alert>}

      <div className="v2-builder">
        {/* Categories */}
        <nav className="v2-builder-nav">
          <div className="v2-section-title">Categories</div>
          {draft.categories.length === 0 && (
            <p className="v2-hint">No categories yet. Add one to start.</p>
          )}
          {draft.categories.map((c) => (
            <button key={c.name} type="button"
                    className={`v2-builder-cat${c.name === current?.name ? " on" : ""}`}
                    onClick={() => setActiveName(c.name)}>
              <span className="v2-builder-cat-name">{c.name}</span>
              <span className={`v2-builder-cat-count${!c.active ? " off" : c.itemIds.length === ITEMS_PER_CATEGORY ? " full" : ""}`}>
                {c.active ? `${c.itemIds.length}/${ITEMS_PER_CATEGORY}` : "Off"}
              </span>
            </button>
          ))}
          {newCategory ? (
            <NewCategoryRow
              existing={draft.categories.map((c) => c.name)}
              onAdd={addCategory}
              onCancel={() => setNewCategory(false)}
            />
          ) : (
            <Button onClick={() => setNewCategory(true)} className="v2-builder-add">
              <TbPlus size={14} /> Add category
            </Button>
          )}
        </nav>

        {/* The nine slots */}
        <section className="v2-builder-main">
          {!current ? (
            <p className="v2-hint">Add a category to start building this template.</p>
          ) : (
            <>
              <div className="v2-builder-head">
                <div>
                  <h3 className="v2-builder-title">{current.name}</h3>
                  <div className="v2-hint">
                    {current.itemIds.length} of {ITEMS_PER_CATEGORY} items selected
                  </div>
                </div>
                <label className="v2-builder-toggle">
                  <input type="checkbox" checked={current.active}
                         onChange={(e) => patchCategory(current.name, { active: e.target.checked })} />
                  Active
                </label>
                <Button variant="danger" onClick={() => removeCategory(current.name)}>Remove</Button>
              </div>

              <div className="v2-progress" role="img"
                   aria-label={`${current.itemIds.length} of ${ITEMS_PER_CATEGORY} items`}>
                <span style={{
                  width: `${Math.min(100, (current.itemIds.length / ITEMS_PER_CATEGORY) * 100)}%`,
                  background: current.itemIds.length === ITEMS_PER_CATEGORY
                    ? "var(--v2-success)"
                    : current.itemIds.length > ITEMS_PER_CATEGORY ? "var(--v2-danger)" : "var(--v2-accent)",
                }} />
              </div>
              {current.itemIds.length > ITEMS_PER_CATEGORY && (
                <Alert tone="warning">
                  This category holds {current.itemIds.length} items. Exactly{" "}
                  {ITEMS_PER_CATEGORY} are needed before the template can be published.
                </Alert>
              )}

              <div className="v2-slots">
                {current.itemIds.map((itemId, index) => {
                  const item = byId.get(itemId);
                  return (
                    <div key={itemId} className="v2-slot">
                      <span className="v2-slot-index">{index + 1}</span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="v2-slot-name">{item ? item.name : `Item ${itemId}`}</div>
                        <div className="v2-slot-meta">
                          {item
                            ? `${["red", "yellow", "green"].filter((r) => (item.statements?.[r]?.summary || "").trim()).length} of 3 statements · importance ${item.default_weight}/5`
                            : "This item no longer exists in the library"}
                        </div>
                      </div>
                      <Button icon aria-label="Move up" disabled={index === 0}
                              onClick={() => move(index, -1)}><TbArrowUp size={14} /></Button>
                      <Button icon aria-label="Move down" disabled={index === current.itemIds.length - 1}
                              onClick={() => move(index, 1)}><TbArrowDown size={14} /></Button>
                      <Button icon aria-label="Remove from this category"
                              onClick={() => removeItem(itemId)}><TbX size={14} /></Button>
                    </div>
                  );
                })}

                {current.itemIds.length < ITEMS_PER_CATEGORY && (
                  <Button onClick={() => setAdding(true)} className="v2-slot-add">
                    <TbPlus size={14} /> Add items
                    <span className="v2-hint" style={{ marginLeft: ".375rem" }}>
                      {ITEMS_PER_CATEGORY - current.itemIds.length} more needed
                    </span>
                  </Button>
                )}
              </div>

              <p className="v2-editor-foot">
                Order is changed with the up and down buttons — it is the order the items
                appear in every assessment created from this template.
              </p>
            </>
          )}
        </section>

        {/* Readiness */}
        <aside className="v2-builder-side">
          <div className="v2-section-title">Template health</div>
          <div className="v2-readiness">
            <div className="v2-ring" style={{ "--score": readiness, "--rc": readiness === 100 ? "var(--v2-success)" : "var(--v2-accent)" }}
                 role="img" aria-label={`${full} of ${activeCategories.length} categories full`}>
              <span>{full}/{activeCategories.length}</span>
            </div>
            <div className="v2-hint">
              {activeCategories.length === 0
                ? "No active category yet."
                : full === activeCategories.length
                  ? "Every active category is full. Ready to publish."
                  : "Each active category needs exactly nine items."}
            </div>
          </div>

          <div className="v2-dotlist">
            {draft.categories.map((c) => (
              <div key={c.name} className="v2-dotrow">
                <span className={`v2-feed-dot${c.active && c.itemIds.length === ITEMS_PER_CATEGORY ? " full" : ""}`} />
                <span style={{ flex: 1, minWidth: 0 }}>{c.name}</span>
                <span className="v2-dim">
                  {c.active ? `${c.itemIds.length}/${ITEMS_PER_CATEGORY}` : "Off"}
                </span>
              </div>
            ))}
          </div>

          {versions.length > 0 && (
            <>
              <div className="v2-section-title" style={{ marginTop: "1rem" }}>Published versions</div>
              <div className="v2-dotlist">
                {versions.map((v) => (
                  <div key={v.id} className="v2-dotrow">
                    <span style={{ flex: 1 }}>Version {v.version}</span>
                    <span className="v2-dim">{v.published_at ? fmtDate(v.published_at) : "—"}</span>
                  </div>
                ))}
              </div>
            </>
          )}
        </aside>
      </div>

      {adding && current && (
        <AddItemsModal
          library={library}
          // Every item already used ANYWHERE in this template, not just in this
          // category. An assessment must not ask the same question twice, and the
          // comparison matches items across reviews by name — duplicates would make two
          // different rows indistinguishable.
          alreadyIn={draft.categories.flatMap((c) => c.itemIds)}
          category={current.name}
          remaining={ITEMS_PER_CATEGORY - current.itemIds.length}
          onClose={() => setAdding(false)}
          onAdd={addItems}
          onLibraryChanged={reloadLibrary}
        />
      )}
    </div>
  );
};

const NewCategoryRow = ({ existing, onAdd, onCancel }) => {
  const [name, setName] = useState("");
  // The score groups the library items are tagged with come first, because a
  // category whose name matches no item's tags can never be filled from the library.
  const suggestions = CATEGORIES.filter((c) => !existing.includes(c));

  return (
    <div className="v2-builder-newcat">
      <input className="v2-input" autoFocus value={name} placeholder="Category name"
             aria-label="New category name"
             onChange={(e) => setName(e.target.value)}
             onKeyDown={(e) => {
               if (e.key === "Enter") onAdd(name);
               if (e.key === "Escape") onCancel();
             }} />
      {suggestions.length > 0 && (
        <div className="v2-chiprow">
          {suggestions.map((c) => (
            <button key={c} type="button" className="v2-chip" onClick={() => onAdd(c)}>{c}</button>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: ".375rem" }}>
        <Button variant="primary" onClick={() => onAdd(name)} disabled={!name.trim()}>Add</Button>
        <Button onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

const AddItemsModal = ({ library, alreadyIn, category, remaining, onClose, onAdd }) => {
  const [picked, setPicked] = useState([]);
  const [search, setSearch] = useState("");
  // Items tagged with this category first: they are the ones meant for it. The rest stay
  // reachable, because a category name is free text and need not match a library tag.
  const available = library
    .filter((i) => i.enabled && !alreadyIn.includes(i.id))
    .filter((i) => !search || i.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => {
      const am = a.categories.includes(category) ? 0 : 1;
      const bm = b.categories.includes(category) ? 0 : 1;
      return am - bm || a.name.localeCompare(b.name);
    });

  const toggle = (id) =>
    setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));

  return (
    <Modal wide title={`Add items to ${category}`} onClose={onClose}
      footer={<>
        <span className="v2-hint" style={{ marginRight: "auto" }}>
          {picked.length} selected · {remaining} more needed
        </span>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="primary" disabled={picked.length === 0} onClick={() => onAdd(picked)}>
          Add {picked.length || ""}
        </Button>
      </>}>
      <Field label="Search the library">
        <input className="v2-input" value={search} placeholder="Backup restore testing"
               onChange={(e) => setSearch(e.target.value)} />
      </Field>

      {available.length === 0 ? (
        <p className="v2-hint">
          {library.length === 0
            ? "The item library is empty. Add items on the Item library tab first."
            : "Every enabled library item is already used somewhere in this template. Add more on the Item library tab."}
        </p>
      ) : (
        <div className="v2-picklist">
          {available.map((i) => (
            <button key={i.id} type="button"
                    className={`v2-pick${picked.includes(i.id) ? " on" : ""}`}
                    aria-pressed={picked.includes(i.id)}
                    onClick={() => toggle(i.id)}>
              <span className={`v2-check${picked.includes(i.id) ? " on" : ""}`} aria-hidden="true" />
              <span style={{ flex: 1, minWidth: 0 }}>
                <span className="v2-slot-name">{i.name}</span>
                <span className="v2-slot-meta">
                  {i.categories.join(", ") || "No category"} · importance {i.default_weight}/5
                </span>
              </span>
            </button>
          ))}
        </div>
      )}
    </Modal>
  );
};

export default TemplateBuilder;
